/**
 * routing.ts — where you are, in the URL.
 *
 * Hash routing, and no router dependency. Two reasons, both about the
 * deployment rather than about taste: the workbench is served as static files
 * behind the same origin as the API (§15.2), so a hash route never reaches the
 * server and cannot be mistaken for an API path; and a diagram open in a
 * browser tab should be a link an engineer can send to a colleague.
 */

import { useEffect, useState } from "react";

export type Route =
  | { name: "projects" }
  | { name: "project"; projectId: string }
  | { name: "diagram"; projectId: string; diagramId: string }
  | { name: "capabilities"; projectId: string }
  | { name: "surfaces"; projectId: string }
  | { name: "surface"; projectId: string; surface: string }
  | { name: "audit"; projectId: string }
  | { name: "changeSet"; projectId: string; changeSetId: string };

export function parseRoute(hash: string): Route {
  const path = hash.replace(/^#/, "").replace(/^\/+/, "");
  const segments = path.split("/").filter(Boolean).map(decodeURIComponent);

  if (segments[0] === "p" && segments[1]) {
    if (segments[2] === "d" && segments[3]) return { name: "diagram", projectId: segments[1], diagramId: segments[3] };
    if (segments[2] === "audit") return { name: "audit", projectId: segments[1] };
    if (segments[2] === "cs" && segments[3]) return { name: "changeSet", projectId: segments[1], changeSetId: segments[3] };
    if (segments[2] === "capabilities") return { name: "capabilities", projectId: segments[1] };
    if (segments[2] === "s") {
      // A surface key is this repository's, not the Engine's, so an unknown key
      // is carried to the page that can say so rather than being dropped here —
      // a 404-shaped blank would be indistinguishable from a missing capability.
      if (segments[3]) return { name: "surface", projectId: segments[1], surface: segments[3] };
      return { name: "surfaces", projectId: segments[1] };
    }
    return { name: "project", projectId: segments[1] };
  }
  return { name: "projects" };
}

export function routeHref(route: Route): string {
  switch (route.name) {
    case "projects": return "#/";
    case "project": return `#/p/${encodeURIComponent(route.projectId)}`;
    case "audit": return `#/p/${encodeURIComponent(route.projectId)}/audit`;
    case "capabilities": return `#/p/${encodeURIComponent(route.projectId)}/capabilities`;
    case "surfaces": return `#/p/${encodeURIComponent(route.projectId)}/s`;
    case "surface": return `#/p/${encodeURIComponent(route.projectId)}/s/${encodeURIComponent(route.surface)}`;
    case "diagram": return `#/p/${encodeURIComponent(route.projectId)}/d/${encodeURIComponent(route.diagramId)}`;
    case "changeSet": return `#/p/${encodeURIComponent(route.projectId)}/cs/${encodeURIComponent(route.changeSetId)}`;
  }
}

export function navigate(route: Route): void {
  window.location.hash = routeHref(route);
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(typeof window === "undefined" ? "" : window.location.hash));
  useEffect(() => {
    const onChange = (): void => setRoute(parseRoute(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}
